"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, Users } from "lucide-react";

const TOTAL = 84.00;
const WAYS = 6;
const PER_PERSON = TOTAL / WAYS;

type Person = {
  initial: string;
  name: string;
  hue: number;
  paid: boolean;
};

const PEOPLE: Person[] = [
  { initial: "M", name: "Maria", hue: 200, paid: true },
  { initial: "J", name: "James", hue: 30, paid: true },
  { initial: "A", name: "Alex", hue: 280, paid: true },
  { initial: "D", name: "Daniela", hue: 340, paid: true },
  { initial: "L", name: "Luis", hue: 150, paid: false },
  { initial: "R", name: "Riya", hue: 50, paid: false },
];

export default function RushHourSplitScroll() {
  const { t } = useLang();
  const paidCount = PEOPLE.filter((p) => p.paid).length;
  const collected = paidCount * PER_PERSON;

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Splits", "Splits")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Split the bill. ", "Divide la cuenta. ")}
            <span className="text-redy-charcoal/30">{t("Six ways.", "En seis.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Drop the iPad on the table. Six people tap. The bill closes itself. No \"who had the agua fresca?\". No mental math. No waiting around for one person to pay for everyone and Venmo-chase them later.",
              "Pones el iPad en la mesa. Seis personas tapean. La cuenta se cierra sola. Sin \"¿quién pidió el agua fresca?\". Sin matemáticas mentales. Sin esperar a que uno pague todo y le ande pidiendo el Venmo después."
            )}
          </p>
        </motion.div>

        {/* Total card + 6 person cards */}
        <div className="max-w-[920px] mx-auto">
          {/* Total / progress card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="bg-[#FAFAF8] rounded-[24px] border border-black/5 shadow-[0_10px_40px_rgba(0,0,0,0.04)] p-6 md:p-8 mb-6"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-redy-secondary/60 mb-1">
                  {t("Table 12 · Friday night", "Mesa 12 · Viernes noche")}
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="text-[40px] md:text-[56px] font-black text-redy-charcoal tabular-nums leading-none">
                    ${TOTAL.toFixed(2)}
                  </div>
                  <div className="text-[14px] font-bold text-redy-secondary">
                    ÷ 6 ={" "}
                    <span className="text-redy-charcoal tabular-nums">${PER_PERSON.toFixed(2)}</span>{" "}
                    {t("each", "c/u")}
                  </div>
                </div>
              </div>

              {/* Progress meter */}
              <div className="min-w-[200px] flex-1 md:flex-none">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-[10px] font-black uppercase tracking-widest text-redy-secondary/60">
                    {t("Collected", "Cobrado")}
                  </span>
                  <span className="text-[12px] font-black text-redy-charcoal tabular-nums">
                    ${collected.toFixed(2)} / ${TOTAL.toFixed(2)}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-redy-charcoal/10 overflow-hidden">
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: collected / TOTAL }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.2, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
                    className="h-full rounded-full origin-left"
                    style={{ background: "linear-gradient(90deg, #F4722B 0%, #D7263D 100%)" }}
                  />
                </div>
                <div className="text-[10px] font-bold mt-1.5" style={{ color: "#D7263D" }}>
                  {paidCount}/{WAYS} {t("paid · 2 tapping now", "pagaron · 2 tapeando")}
                </div>
              </div>
            </div>
          </motion.div>

          {/* 6 person cards — 3 cols on md, 2 cols on mobile */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {PEOPLE.map((person, i) => (
              <motion.div
                key={person.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.5,
                  delay: 0.15 + i * 0.08,
                  ease: [0.21, 0.47, 0.32, 0.98],
                }}
                className="bg-[#FAFAF8] rounded-[20px] p-5 border border-black/5 relative overflow-hidden"
              >
                {/* Subtle paid glow */}
                {person.paid && (
                  <div
                    className="absolute -top-8 -right-8 w-[80px] h-[80px] rounded-full pointer-events-none"
                    style={{ background: "radial-gradient(circle, rgba(46,204,113,0.15) 0%, transparent 70%)" }}
                  />
                )}

                <div className="relative">
                  {/* Avatar */}
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-[16px] mb-3"
                    style={{ background: `hsl(${person.hue}, 60%, 55%)` }}
                  >
                    {person.initial}
                  </div>

                  {/* Name */}
                  <div className="text-[13px] font-black text-redy-charcoal mb-1">{person.name}</div>

                  {/* Amount */}
                  <div className="text-[22px] font-black text-redy-charcoal tabular-nums leading-none mb-3">
                    ${PER_PERSON.toFixed(2)}
                  </div>

                  {/* Status pill */}
                  {person.paid ? (
                    <motion.div
                      initial={{ scale: 0.7, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 0.4, delay: 0.5 + i * 0.08 }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider"
                      style={{ backgroundColor: "rgba(46,204,113,0.12)", color: "#2ECC71" }}
                    >
                      <Check className="w-3 h-3" strokeWidth={3.5} />
                      {t("Paid · Apple Pay", "Pagado · Apple Pay")}
                    </motion.div>
                  ) : (
                    <motion.div
                      animate={{
                        boxShadow: [
                          "0 0 0 0 rgba(244,114,43,0.4)",
                          "0 0 0 6px rgba(244,114,43,0)",
                          "0 0 0 0 rgba(244,114,43,0)",
                        ],
                      }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-redy-charcoal text-white text-[11px] font-black"
                    >
                      <svg width="22" height="9" viewBox="0 0 22 9" fill="white" aria-hidden>
                        <text x="0" y="8" fontSize="9" fontWeight="700" fontFamily="-apple-system,sans-serif">
                          Pay
                        </text>
                      </svg>
                      <span className="text-[10px] uppercase tracking-wider">
                        {t("Tap to pay", "Tap para pagar")}
                      </span>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Below caption */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#FAFAF8] px-5 py-3 rounded-full border border-black/5">
            <Users className="w-4 h-4" style={{ color: "#D7263D" }} />
            <span className="text-[13px] font-bold text-redy-charcoal">
              {t(
                "Split by item, by seat, by share. Apple Pay, card, cash — everyone, whatever they want.",
                "Divide por item, por silla o por partes. Apple Pay, tarjeta, efectivo — cada quien, como quiera."
              )}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
